import { useRef, useEffect, useMemo, useState } from "react";
import { AlertService } from "../../../services/Alert";
import { QRCodeSVG } from 'qrcode.react';
import TextArea from "../../../components/form-controls/TextArea";
import { Controller, useForm } from "react-hook-form";
import Select from 'react-select'
import Button from "../../../components/form-controls/Button";
import { ShortLinksService } from "../../../services/ShortLinksService";
import { SubAccountService } from '../../../services/SubaccountService';
import { useCurrentAccount } from "../../../hooks/useCurrentAccount";
import useModal from "../../../hooks/useModal";
import InvalidFeedback from "../../../components/InvalidFeedback";
import { useLocation } from "react-router-dom";
import { APPS } from "../../../constants";
import { useTranslation } from "react-i18next";
import Switch from "../../../components/form-controls/Switch";
import Input from "../../../components/form-controls/Input";
import Calendar from "../../../components/Calendar";

declare const bootstrap: any;

const templateCode = `
<script src="https://voices-dev.github.io/contacto_wa.js"></script>
<script>
    document.addEventListener("DOMContentLoaded", (event) =>  voicesWhatsApp("NUMBER","MESSAGE","TOKEN") )
</script>
`

interface EditLinkModalProps {
    state: { show: boolean, link: any };
    onRequestClose: (refresh: boolean) => void;
}

function EditLinkModal({ state, onRequestClose }: EditLinkModalProps) {

    const { t } = useTranslation();

    const { account } = useCurrentAccount();

    const modal = useRef<HTMLDivElement>(null);

    const { state: { channels } } = useLocation()

    useModal(modal, state.show)

    const defaultValues = {
        message: '',
        app: '',
        advanced_follow: false,
        label: '',
        trigger: '',
        sub_account_id: 0,
        ttl_enabled: false,
        ttl_date: null,
        message_ttl: ''
    }

    const [subaAccounts, setsubAccounts] = useState([]);
    const opciones = [
        { label: t("platform.channels.edit-modal.trigger-options.sent_subaccount"), value: "sent_subaccount" },
        { label: t("platform.channels.edit-modal.trigger-options.sent_agent"), value: "sent_agent" },
        { label: t("platform.channels.edit-modal.trigger-options.none"), value: "none" }
    ];



    const { control, register, watch, setValue, handleSubmit, reset, formState: { errors, isValid, isSubmitting } } = useForm({
        mode: 'onChange',
        defaultValues
    })
    const whatsappStandar = useMemo(() => channels.find(c => c.app.toUpperCase() === APPS.WHATSAPP_STANDAR), [channels])
    const whatsappBusiness = useMemo(() => channels.find(c => c.app.toUpperCase() === APPS.WHATSAPP_BUSINESS), [channels])
    const code = useMemo(() => {
        if (!state.link) return templateCode;

        const number = state.link.app === 'wa-business' ? whatsappBusiness.meta_phone_number : whatsappStandar.number;

        return templateCode
            .replace('NUMBER', number)
            .replace('MESSAGE', state.link.message)
            .replace('TOKEN', state.link.token)

    }, [state.link])

    const advancedFollowshow = watch('advanced_follow');
    const subAccountShow = watch('sub_account_id');
    const triggerShow = watch('trigger');
    const ttlEnabled = watch('ttl_enabled');

    const getSubAccounts = async () => {
        const { data } = await SubAccountService.getSubaccounts(account.id);
        setsubAccounts(data.sub_accounts.map(subaccount => ({
            value: subaccount.id,
            label: subaccount.name
        })));
    };

    const submit = async (data) => {
        try {
            data.advanced_follow = data.advanced_follow ? 1 : 0;
            data.ttl_enabled = data.ttl_enabled ? 1 : 0;
            let expireAt = null;
            if (data.ttl_enabled && data.ttl_date) {
                expireAt = data.ttl_date.toISOString().split('T')[0];
            }
            await ShortLinksService.update(state.link.account_id, data.message, state.link.token, data.app, data.advanced_follow, data.label, data.trigger, data.sub_account_id, expireAt, data.message_ttl);
            await onRequestClose(true)
            AlertService.toastSuccess(t("platform.channels.edit-modal.toast-update-success"))
        } catch (error) {
            console.error(error);
            AlertService.error('¡Error!', error)
        }
    }


    const copyToClipboard = async (text) => {
        await navigator.clipboard.writeText(text);

        AlertService.toastSuccess(t("platform.channels.edit-modal.toast-copied"), 2000)
    }

    const handleTTLDateChange = (date) => {
        setValue('ttl_date', date, { shouldValidate: true });
    };

    useEffect(() => {
        if (!state.link) return;

        const toLocalDateOnly = (raw: unknown): Date | null => {
            if (!raw) return null;
            if (raw instanceof Date) return raw;

            const s = String(raw).trim();

            const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
            if (m) {
                const [, y, mo, d] = m;
                return new Date(Number(y), Number(mo) - 1, Number(d));
            }

            const d2 = new Date(s);
            return isNaN(d2.getTime()) ? null : d2;
        };

        const ttlDate = toLocalDateOnly(state.link.expire_at);
        const ttlEnabled = !!ttlDate;

        reset({
            message: state.link.message ?? '',
            app: state.link.app ?? '',
            advanced_follow: !!state.link.advanced_follow,
            label: state.link.label ?? '',
            trigger: state.link.trigger ?? '',
            sub_account_id: state.link.sub_account_id ?? 0,
            ttl_enabled: ttlEnabled,
            ttl_date: ttlDate,
            message_ttl: state.link.message_ttl ?? ''
        });
    }, [state.link, reset]);




    useEffect(() => {
        if (!advancedFollowshow) {
            setValue('advanced_follow', false);
            setValue('label', defaultValues.label);
            setValue('sub_account_id', defaultValues.sub_account_id);
            setValue('trigger', defaultValues.trigger);
        }
    }, [advancedFollowshow, setValue]);


    useEffect(() => {
        if (triggerShow !== 'sent_subaccount') {
            setValue('sub_account_id', 0);
        }
    }, [triggerShow, setValue]);

    useEffect(() => {
        if (!subAccountShow) {
            setValue('sub_account_id', 0);
        }
    }, [subAccountShow, setValue]);

    useEffect(() => {
        if (!ttlEnabled) {
            setValue('ttl_date', null);
            setValue('message_ttl', '');
        }
    }, [ttlEnabled, setValue]);


    useEffect(() => {
        getSubAccounts();
    }, [account]);


    return (
        <div className="modal fade" ref={modal} data-bs-backdrop="static" data-bs-keyboard="false">
            <div className="modal-dialog modal-lg" role="document">
                <div className="modal-content ">
                    <div className="modal-header">
                        <h4 className="modal-title">{t('platform.channels.edit-modal.edit-title')}</h4>
                        <button type="button" className="btn-close" onClick={() => onRequestClose(false)}></button>
                    </div>
                    <div className="modal-body">
                        <div className="text-center navbar-voices rounded-1 border p-4" >
                            <div className="mb-3">
                                <QRCodeSVG id={`qr-img-${state?.link?.token}`} value={`https://${state?.link?.link}`} size={200} />
                            </div>
                            <button type='button' className='btn btn-ghost-primary text-white-dark btn-lg' onClick={() => copyToClipboard(state?.link?.link)}><i className="bi bi-files"></i>{t('platform.channels.edit-modal.download-btn')}</button>
                        </div>

                        <hr />
                        <div className="mb-3">
                            <div className="d-flex align-self-center">
                                <div className="flex-grow-1 me-3">
                                    <label className="form-label" >{t('platform.channels.edit-modal.link-label')}</label>
                                    <input type="text" className="form-control" disabled defaultValue={state?.link?.link} />
                                </div>

                                <button type='button' className='btn btn-soft-primary text-white-dark align-self-end' onClick={() => copyToClipboard(state?.link?.link)}><i className="bi bi-files"></i>{t('platform.channels.edit-modal.copy-btn')}</button>
                            </div>


                        </div>
                        <hr />

                        <label className="form-label" >{t('platform.channels.edit-modal.insert-code-label')}</label>
                        <p>{t('platform.channels.edit-modal.insert-text')}</p>

                        <div className="mb-3">
                            <div className='navbar-voices rounded-1 border relative'>
                                <button type='button' className='btn btn-ghost-primary btn-lg fix-top-right text-white-dark' onClick={() => copyToClipboard(code)}><i className="bi bi-files"></i>{t('platform.channels.edit-modal.copy-text')} </button>
                                <pre className='monospace p-3' >
                                    {code}
                                </pre>
                            </div>
                        </div>

                        <hr />
                        <div className="mb-3">
                            <Switch label={t("platform.channels.edit-modal.advanced-follow-label")} {...register('advanced_follow')} />
                        </div>
                        {advancedFollowshow && (
                            <>
                                <div className="mb-3">
                                    <Input label={t("platform.channels.edit-modal.label-label")} placeholder={t("platform.channels.edit-modal.label-placeholder")} errors={errors.label} {...register('label', { required: true })} />
                                </div>

                                <div className="mb-3">
                                    <label className="form-label">{t("platform.channels.edit-modal.trigger-label")}</label>
                                    <Controller
                                        control={control}
                                        name='trigger'
                                        rules={{ required: true }}
                                        render={({ field: { onChange, value }, fieldState: { error } }) => (
                                            <>
                                                <Select
                                                    options={opciones}
                                                    className={error ? 'form-select-custom is-invalid' : 'form-select-custom'}
                                                    classNamePrefix='form-select-custom'
                                                    placeholder={t("platform.channels.edit-modal.trigger-placeholder")}
                                                    onChange={(selectedOption) => { onChange(selectedOption.value); }}
                                                    value={opciones.find(option => option.value === value) || null}
                                                />
                                                <InvalidFeedback error={error} />
                                            </>
                                        )}
                                    />
                                </div>
                                {triggerShow === 'sent_subaccount' && (
                                    <div className="mb-3">
                                        <label className="form-label">{t("platform.channels.edit-modal.subaccount-label")}</label>
                                        <Controller
                                            control={control}
                                            name='sub_account_id'
                                            rules={{ required: true }}
                                            render={({ field: { onChange, value }, fieldState: { error } }) => (
                                                <>
                                                    <Select
                                                        options={subaAccounts}
                                                        className={error ? 'form-select-custom is-invalid' : 'form-select-custom'}
                                                        classNamePrefix='form-select-custom'
                                                        placeholder={t("platform.channels.edit-modal.subaccount-placeholder")}
                                                        onChange={(selectedOption) => onChange(selectedOption.value)}
                                                        value={subaAccounts.find(option => option.value === value) || null}
                                                    />
                                                    <InvalidFeedback error={error} />
                                                </>
                                            )}
                                        />
                                    </div>
                                )}
                            </>
                        )}

                        <div className="mb-3">
                            <TextArea placeholder={t("platform.channels.edit-modal.message-placeholder")} label={t('platform.channels.edit-modal.message-label')} errors={errors.message}  {...register('message', { required: true })} />
                        </div>

                        <div className="mb-3">
                            <Switch
                                label={t("platform.channels.edit-modal.ttl-switch")}
                                {...register('ttl_enabled')}
                            />
                            {ttlEnabled && (
                                <>
                                    <div className="mt-3 mb-5">
                                        <p className="text-muted small mb-2">
                                            {t("platform.channels.edit-modal.ttl-help")}
                                        </p>
                                        <Controller
                                            control={control}
                                            name='ttl_date'
                                            rules={{
                                                required: ttlEnabled ? t("platform.channels.edit-modal.ttl-required") : false
                                            }}
                                            render={({ field: { value }, fieldState: { error } }) => (
                                                <div className="d-flex gap-4 align-items-center justify-content-center">
                                                    <div className="row d-flex align-items-center">
                                                        <div className="col-12">
                                                            <Calendar
                                                                selectedDate={value}
                                                                onDateChange={handleTTLDateChange}
                                                                minDate={new Date()}
                                                            />
                                                            <InvalidFeedback error={error} />
                                                        </div>
                                                    </div>
                                                    {value && (
                                                        <div className="col-4 d-flex align-items-center">
                                                            <div className="card border-0 bg-light w-100">
                                                                <div className="card-body d-flex flex-column justify-content-center">
                                                                    <div className="text-center">
                                                                        <i className="bi bi-calendar-check text-success fs-1 mb-3"></i>
                                                                        <h6 className="text-primary mb-2">{t("platform.channels.edit-modal.ttl-picked-title")}</h6>
                                                                        <p className="text-dark fw-bold mb-1">
                                                                            {value.toLocaleDateString('es-ES', {
                                                                                day: 'numeric',
                                                                                month: 'long',
                                                                                year: 'numeric'
                                                                            })}
                                                                        </p>
                                                                        <small className="text-muted">
                                                                            {value.toLocaleDateString('es-ES', { weekday: 'long' })}
                                                                        </small>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        />
                                    </div>
                                    <div className="mb-3">
                                        <TextArea label={t("platform.channels.links-page.create-modal.message-ttl-title")} placeholder={t("platform.channels.links-page.create-modal.message-ttl-placeholder")} errors={errors.message_ttl} {...register('message_ttl', { required: false })} />
                                    </div>
                                </>
                            )}
                        </div>

                        {/* <div className="mb-3">
                            <label className="form-label" >Canal *</label>
                            <select className={'form-control ' + (errors?.app ? 'is-invalid' : '')}  {...register('app', { required: true })}>
                                <option disabled value={''}>Elija una opción</option>
                                <option value={'waweb'}>WhatsApp Estandar</option>
                                <option value={'wa-business'}>WhatsApp Business</option>
                            </select>
                            <InvalidFeedback error={errors.app} />
                        </div> */}

                    </div>

                    <div className="modal-footer">
                        <button type="button" className="btn btn-white btn-sm" onClick={() => onRequestClose(false)}>{t('btn-cancel')} </button>
                        <Button type="submit" className="btn btn-primary btn-sm" disabled={!isValid} isLoading={isSubmitting} onClick={handleSubmit(submit)}>{t('btn-save')}</Button>
                    </div>
                </div>
            </div>
        </div>
    )
}
export default EditLinkModal